import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import MuiTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import LockIcon from '@mui/icons-material/Lock';
import Spinner from '../../../components/Spinner';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import Input from '../../../components/Input';
import StatusBadge from '../../../components/StatusBadge';
import Toast from '../../../components/Toast';
import OverrideModal from '../../../components/OverrideModal';
import FeatureFlagService from '../../../services/feature-flag.service';
import { usePermission } from '../../../hooks/usePermission';
import { Modules, AuthRoutes, RouteParamsTypes } from '../../../enums';

type TabKey = 'overview' | 'overrides';

const FeatureFlagDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canManage = usePermission('feature-flag:manage');
  const canKill = usePermission('feature-flag:kill');

  const [tab, setTab] = useState<TabKey>('overview');
  const [flag, setFlag] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState<any[]>([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [killModalOpen, setKillModalOpen] = useState(false);
  const [killReason, setKillReason] = useState('');
  const [killSubmitting, setKillSubmitting] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    FeatureFlagService.get(id)
      .then((response: any) => setFlag(response?.data ?? null))
      .finally(() => setLoading(false));
  }, [id]);

  const loadOverrides = useCallback(() => {
    if (!id) return;
    setOverridesLoading(true);
    FeatureFlagService.listOverrides(id)
      .then((response: any) => setOverrides(response?.data?.items ?? []))
      .finally(() => setOverridesLoading(false));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 'overrides') loadOverrides();
  }, [tab, loadOverrides]);

  const handleRemoveOverride = useCallback(
    async (tenantId: number) => {
      if (!id) return;
      await FeatureFlagService.removeOverride(id, tenantId);
      toast.success(<Toast message={t('featureFlags.detail.pageTexts.overrideRemoved')} />, { icon: false });
      loadOverrides();
    },
    [id, loadOverrides, t]
  );

  const handleKillOrLift = useCallback(async () => {
    if (!id || !flag) return;
    if (!killReason.trim()) return;
    setKillSubmitting(true);
    try {
      if (flag.is_killed) {
        await FeatureFlagService.liftKill(id, killReason);
        toast.success(<Toast message={t('featureFlags.detail.pageTexts.killLifted')} />, { icon: false });
      } else {
        await FeatureFlagService.kill(id, killReason);
        toast.success(<Toast message={t('featureFlags.detail.pageTexts.killed')} />, { icon: false });
      }
      setKillModalOpen(false);
      setKillReason('');
      load();
    } finally {
      setKillSubmitting(false);
    }
  }, [id, flag, killReason, load, t]);

  if (loading || !flag) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <h4 className="m-0">{flag.flag_name}</h4>
          <span className="text-muted">{flag.flag_code}</span>
          {flag.status_name && flag.status_colour && <StatusBadge label={flag.status_name} colour={flag.status_colour} />}
          {flag.is_killed && (
            <span className="badge bg-danger d-flex align-items-center gap-1">
              <LockIcon fontSize="small" />
              {t('featureFlags.detail.pageTexts.killedBadge')}
            </span>
          )}
        </div>
        <div className="d-flex gap-2">
          {canManage && (
            <Button
              className="btn btn-outline-secondary"
              text={t('featureFlags.detail.pageTexts.edit')}
              onClick={() => navigate(`${Modules.home}${AuthRoutes.featureFlags}${RouteParamsTypes.edit}/${id}`)}
            />
          )}
          {canKill && (
            <Button
              className={`btn ${flag.is_killed ? 'btn-outline-success' : 'btn-danger'}`}
              text={flag.is_killed ? t('featureFlags.detail.pageTexts.liftKillButton') : t('featureFlags.detail.pageTexts.killButton')}
              onClick={() => setKillModalOpen(true)}
            />
          )}
        </div>
      </div>

      <div className="card mb-6">
        <MuiTabs value={tab} onChange={(_e, value) => setTab(value)} textColor="primary">
          <Tab value="overview" label={t('featureFlags.detail.pageTexts.tabs.overview')} />
          <Tab value="overrides" label={t('featureFlags.detail.pageTexts.tabs.overrides')} />
        </MuiTabs>
        <hr className="m-0" />

        <div className="card-body">
          {tab === 'overview' && (
            <div className="row g-4">
              <div className="col-md-3">
                <label className="form-label d-block text-muted mb-1">{t('featureFlags.form.pageTexts.fields.product.label')}</label>
                <div>{flag.product_name ?? '—'}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label d-block text-muted mb-1">{t('featureFlags.form.pageTexts.fields.defaultState.label') || 'Default State'}</label>
                <div>{flag.default_state ? t('featureFlags.list.pageTexts.defaultOn') : t('featureFlags.list.pageTexts.defaultOff')}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label d-block text-muted mb-1">{t('featureFlags.form.pageTexts.fields.targeting.label')}</label>
                <div>{flag.targeting}</div>
              </div>
              <div className="col-md-3">
                <label className="form-label d-block text-muted mb-1">{t('featureFlags.form.pageTexts.fields.status.label')}</label>
                <div>{flag.status_name ?? '—'}</div>
              </div>
              <div className="col-md-6">
                <label className="form-label d-block text-muted mb-1">{t('featureFlags.form.pageTexts.fields.description.label')}</label>
                <div>{flag.description ?? '—'}</div>
              </div>
              <div className="col-md-6">
                <label className="form-label d-block text-muted mb-1">{t('featureFlags.form.pageTexts.fields.notes.label')}</label>
                <div>{flag.notes ?? '—'}</div>
              </div>
            </div>
          )}

          {tab === 'overrides' && (
            <div>
              {canManage && (
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    className="btn btn-primary"
                    text={t('featureFlags.detail.pageTexts.addOverride')}
                    onClick={() => setShowOverrideModal(true)}
                  />
                </div>
              )}
              {overridesLoading ? (
                <Spinner />
              ) : (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>{t('subscriptions.list.pageTexts.table.columns.tenant')}</th>
                      <th>{t('featureFlags.detail.pageTexts.overrideState')}</th>
                      <th>{t('featureFlags.detail.pageTexts.reason')}</th>
                      {canManage && <th className="text-end">{t('featureFlags.list.pageTexts.table.columns.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {overrides.map((row) => (
                      <tr key={row.id}>
                        <td>{row.tenant_name ?? '—'}</td>
                        <td>{row.override_state ? t('featureFlags.list.pageTexts.defaultOn') : t('featureFlags.list.pageTexts.defaultOff')}</td>
                        <td>{row.reason}</td>
                        {canManage && (
                          <td className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-icon"
                              onClick={() => handleRemoveOverride(row.tenant_id)}
                              aria-label="remove"
                            >
                              <i className="ri-delete-bin-line" />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                    {overrides.length === 0 && (
                      <tr>
                        <td colSpan={canManage ? 4 : 3} className="text-muted text-center">
                          {t('featureFlags.detail.pageTexts.noOverrides')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>

      <OverrideModal
        open={showOverrideModal}
        flagId={flag.id}
        flagCode={flag.flag_code}
        onClose={() => setShowOverrideModal(false)}
        onSaved={() => {
          setShowOverrideModal(false);
          loadOverrides();
        }}
      />

      <Modal
        open={killModalOpen}
        onClose={() => setKillModalOpen(false)}
        title={flag.is_killed ? t('featureFlags.detail.pageTexts.liftKillButton') : t('featureFlags.detail.pageTexts.killButton')}
        content={
          <>
            <p>{flag.is_killed ? t('featureFlags.detail.pageTexts.liftKillWarning') : t('featureFlags.detail.pageTexts.killWarning')}</p>
            <Input
              id="kill_reason"
              name="kill_reason"
              label={t('featureFlags.detail.pageTexts.reason')}
              required
              value={killReason}
              onChange={(e) => setKillReason(e.target.value)}
            />
          </>
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('featureFlags.form.pageTexts.buttons.cancel')} onClick={() => setKillModalOpen(false)} />
            <Button
              className="btn btn-danger"
              text={t('featureFlags.detail.pageTexts.confirm')}
              loading={killSubmitting}
              disabled={!killReason.trim()}
              onClick={handleKillOrLift}
            />
          </>
        }
      />
    </>
  );
};

export default FeatureFlagDetail;
